import { Storybook } from "@tento-ui/ui/storybook";
import { MoreIndicator } from "@tento-lona/sheets-ui";
import { DateTime, NaiveDate } from "@tento-chrono";
import { type EventCategory } from "@tento-lona";
import {
  AuthId,
  CalendarEvent,
  CalendarId,
  EventId,
  EventItem,
  EventItemAllDay,
  EventOrigin,
  RowLocalId,
} from "@tento-lona/sheets";
import { STORYBOOK_EVENT_PRESENTATION } from "../storybook-presentation";

// Event color is now derived from the event's category (via
// EventItemPresentation.backgroundColor → EventCategoryItem color), so the storybook
// builds real full-day events with varied categories rather than stub objects
// carrying a `backgroundColor` field.
const ORIGIN = EventOrigin.backingRow(
  new RowLocalId.Registry().resolveFromRawId("storybook-mock"),
);

let mockSeq = 0;

function mockEvent(category: EventCategory): EventItemAllDay {
  const today = NaiveDate.today();
  return EventItem.make(
    CalendarEvent.of({
      id: EventId.of(`mock-${category}-${mockSeq++}`),
      title: category,
      start: DateTime.utc.now(),
      end: DateTime.utc.now(),
      authId: AuthId.of("mock"),
      calendarId: CalendarId.of("mock"),
      startDate: today,
      endDate: today.addDays(1),
      category,
    }),
    null,
    ORIGIN,
  ) as EventItemAllDay;
}

export class MoreIndicatorStoryBook extends Storybook.Default {
  title(): string {
    return "More Indicator";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Single Event",
        render: () => {
          const $indicator = MoreIndicator.make();
          $indicator.bind(
            [mockEvent("work")],
            STORYBOOK_EVENT_PRESENTATION,
            () => {},
            () => {}, null,
          );
          return $indicator;
        },
      },
      {
        title: "Multiple Events",
        render: () => {
          const $indicator = MoreIndicator.make();
          $indicator.bind(
            [
              mockEvent("social"),
              mockEvent("work"),
              mockEvent("exercise"),
            ],
            STORYBOOK_EVENT_PRESENTATION,
            () => {},
            () => {}, null,
          );
          return $indicator;
        },
      },
      {
        title: "Many Events",
        render: () => {
          const $indicator = MoreIndicator.make();
          $indicator.bind(
            [
              mockEvent("transportation"),
              mockEvent("food"),
              mockEvent("social"),
              mockEvent("work"),
              mockEvent("creative"),
            ],
            STORYBOOK_EVENT_PRESENTATION,
            () => {},
            () => {}, null,
          );
          return $indicator;
        },
      },
      {
        title: "Max Dots (3 of 5)",
        render: () => {
          const $indicator = MoreIndicator.make();
          $indicator.bind(
            [
              mockEvent("transportation"),
              mockEvent("food"),
              mockEvent("social"),
              mockEvent("work"),
              mockEvent("creative"),
            ],
            STORYBOOK_EVENT_PRESENTATION,
            () => {},
            () => {},
            3,
          );
          return $indicator;
        },
      },
      {
        title: "Max Dots (2 of 6)",
        render: () => {
          const $indicator = MoreIndicator.make();
          $indicator.bind(
            [
              mockEvent("transportation"),
              mockEvent("food"),
              mockEvent("social"),
              mockEvent("work"),
              mockEvent("creative"),
              mockEvent("errands"),
            ],
            STORYBOOK_EVENT_PRESENTATION,
            () => {},
            () => {},
            2,
          );
          return $indicator;
        },
      },
      {
        title: "Max Dots (no overflow)",
        render: () => {
          const $indicator = MoreIndicator.make();
          $indicator.bind(
            [mockEvent("work"), mockEvent("exercise")],
            STORYBOOK_EVENT_PRESENTATION,
            () => {},
            () => {},
            5,
          );
          return $indicator;
        },
      },
    ];
  }
}
